import React, { useMemo } from 'react';
import useCustomTable from '../tables/useCustomTable';
import { OsIcon } from '../OsIcon';
import Table from '../tables/Table';
import FilterButton, { FilterGroup } from '../FilterButton';
import TableSearch from '../tables/TableSearch';
import { useGetPublisherAppsQuery } from '../../api/publisherApiSlice';
import { useTranslation } from 'react-i18next';
import Block from '../Block';
import { Loader } from '../components';
import { useHistory } from 'react-router-dom';
import versionCompare from '../../services/versionCompare';
import OptInRateCell from '../metrics/tables/cells/OptInRateCell';
import EnvironmentCell from '../metrics/tables/cells/EnvironmentCell';
import DevicesCell from '../metrics/tables/cells/DevicesCell';
import Pagination from "../tables/Pagination";
import SdkCell from '../tables/SdkCell';

export default function AppTable ( { publisherId, startDate, endDate } ) {

  const { t, i18n } = useTranslation();
  const {
    tableProps,
    paginationProps,
    tableFilterProps,
    tableSearchProps
  } = useCustomTable({
    sortable: true,
    paginate: true,
    sortBy: {name: 'asc'}
  });
  const {filters, setAllFilters} = tableFilterProps
  const history = useHistory();
  const { data = [], isSuccess, isLoading, refetch } = useGetPublisherAppsQuery({publisherId, startDate, endDate})

  const tableData = useMemo(()=>{
    return data.map((row) => {
      return {
        ...row,
        opt_in_geolocated_devices: row.location_authorized_devices/row.active_devices,
        ref_opt_in_geolocated_devices: row.ref_location_authorized_devices/row.ref_active_devices,
        loc_consent_rate: row.loc_consented_devices/row.location_authorized_devices,
        ref_loc_consent_rate: row.ref_loc_consented_devices/row.ref_location_authorized_devices,
        consent_rate: row.location_authorized_devices/row.consented_devices,
        ref_consent_rate: row.ref_location_authorized_devices/row.ref_consented_devices,
      }
    })
  }, [data])

  const formatAppName = (app) => ( app.name ?? app.bundle_id ?? '[MISSING]' )

  const toFloat = (val) => {
    if (isNaN(val)) { return -1 }
    return parseFloat(val)
  }

  const columns = useMemo(() => [{
    Header: t("publisher.app.name"),
    Cell: ({row}) => (String(formatAppName(row.original))),
    accessor: 'name',
    sortType: (rowA, rowB, id, desc) => (formatAppName(rowB.original).localeCompare(formatAppName(rowA.original)))
  },{
    Header: t("publisher.app.os"),
    Cell: ({row}) => (<OsIcon os={row.original.os} />),
    accessor: 'os',
  },{
    Header: t("publisher.app.environment"),
    Cell: ({row}) => (<EnvironmentCell row={row.original} i18n={i18n} />),
    accessor: 'environment',
  },{
    Header: t("publisher.app.production_sdk"),
    Cell: ({row}) => (<SdkCell sdk={row.original.prod_sdk} i18n={i18n} />),
    accessor: 'production_sdk',
    sortType: (rowA, rowB, id, desc) => (versionCompare(rowB.original.prod_sdk.version,rowA.original.prod_sdk.version))
  },{
    Header: t("publisher.app.test_sdk"),
    Cell: ({row}) => (<SdkCell sdk={row.original.test_sdk} i18n={i18n} />),
    accessor: 'test_sdk',
    sortType: (rowA, rowB, id, desc) => (versionCompare(rowB.original.test_sdk.version,rowA.original.test_sdk.version))
  },{
    Header: t("stats.devices"),
    Cell: ({row}) => (<DevicesCell devices={row.original.active_devices} refDevices={row.original.ref_active_devices} />),
    accessor: 'active_devices',
  },{
    Header: t("stats.geolocated_devices"),
    Cell: ({row}) => (<DevicesCell devices={row.original.location_authorized_devices} refDevices={row.original.ref_location_authorized_devices} />),
    accessor: 'location_authorized_devices',
  },{
    Header: t("stats.opt_in_geolocated_devices"),
    Cell: ({row}) => (
        <OptInRateCell val={row.original.opt_in_geolocated_devices} refVal={row.original.ref_opt_in_geolocated_devices} />
      ),
    accessor: 'opt_in_geolocated_devices',
    sortType: (rowA, rowB, id, desc) => (Math.sign(toFloat(rowB.original.opt_in_geolocated_devices) - toFloat(rowA.original.opt_in_geolocated_devices)))
  },{
    Header: t("stats.consent"),
    Cell: ({row}) => (<DevicesCell devices={row.original.loc_consented_devices} refDevices={row.original.ref_loc_consented_devices} />),
    accessor: 'loc_consented_devices',
  },{
    Header: t("stats.consent_rate"),
    Cell: ({row}) => (<OptInRateCell val={row.original.loc_consent_rate} refVal={row.original.ref_loc_consent_rate} />),
    accessor: 'loc_consent_rate',
    sortType: (rowA, rowB, id, desc) => (Math.sign(toFloat(rowB.original.loc_consent_rate) - toFloat(rowA.original.loc_consent_rate)))
  },{
    Header: t("publisher.app.cmp"),
    accessor: 'cmp_provider_name',
  }]);

  const showApp = (rowData) => history.push(`/apps/${rowData.slug}`)

  return (
    <>
      <FilterGroup {...tableFilterProps}>
        <FilterButton
            title={t("publisher.environment")}
            options={["Created", "Test", "Production"]}
            search={false}
            includeAll={false}
            name="environment"/>
        <TableSearch {...tableSearchProps} style={{float: "right"}}/>
      </FilterGroup>
      <Block className="mb-0">
        <Loader success={!isLoading && isSuccess} error={!isLoading && !isSuccess} retry={refetch}>
          <Table
            noPadding
            data={tableData}
            columns={columns}
            {...tableProps}
            initialFilters={filters}
            onRowClick={showApp} />
        </Loader>
      </Block>
      <Pagination {...paginationProps}/>
    </>
  )
}
